/** The Pixi implementation of the scene backend: one display object per rendered node. */

import { Container, Graphics, Rectangle, Text } from "pixi.js";

import type { SceneReport } from "./scene/report";
import { sceneReport } from "./scene/report";
import type { SceneBackend } from "./scene/reconcile";
import type { RenderNode } from "./scene/sceneTree";
import type { SurfaceLayer } from "./surface/surfaceLayer";
import type { Presentation, Pulse } from "./presentations";
import { IDENTITY, presentationNamed, pulseAt } from "./presentations";
import type { Screen, StyleTokens } from "./pixiStyle";
import {
  alignmentToken,
  anchorFor,
  colorToken,
  fontSize,
  numberToken,
  opacity,
  pixels,
} from "./pixiStyle";

const WEIGHTS = ["normal", "bold", "bolder", "lighter"] as const;

type Weight = (typeof WEIGHTS)[number];

/** What a click reached: one node the scene drew, or the empty space of a document. OVL-026. */
export type PickTarget = "node" | "document";

/** The cue playing on one node: its shape, when it started, and where it is now. */
interface LivePulse {
  readonly shape: Presentation;
  /** Taken from the first frame, so this holds no clock of its own. */
  startedAt: number | undefined;
  at: Pulse;
}

/** The longest a single interpolation leg runs, regardless of the measured gap. RUN-045. */
const MAX_INTERP_MS = 1000;

/** The kinds RUN-045 interpolates are every other drawn kind. */
const NOT_INTERPOLATED_KINDS = new Set<PixiHandle["kind"]>(["cells", "surface"]);

interface HandleBase {
  readonly view: Container;
  readonly parent: Container;
  destroyed: boolean;
  /** True for a document's own root: the placement one activity draws inside. */
  root: boolean;
  /** Where the last draw put this, before any cue moved it. */
  baseX: number;
  baseY: number;
  baseAlpha: number;
  /** The centre of the node's box in pixels, which is what a cue scales about. */
  centreX: number;
  centreY: number;
  pulse: LivePulse | undefined;
  /** The node this handle last drew, for a tick-driven repaint that carries no sample of its own. */
  node: RenderNode | undefined;
  /** Normalized box and, for a `bar`, fill: the interpolation leg's start. */
  fromLeft: number;
  fromTop: number;
  fromWidth: number;
  fromHeight: number;
  fromFill: number;
  /** Normalized box and fill the leg above animates toward: the latest sample received. */
  toLeft: number;
  toTop: number;
  toWidth: number;
  toHeight: number;
  toFill: number;
  /** Wall-clock ms the current leg started, or undefined once settled at `to`. */
  interpAt: number | undefined;
  /** How long the current leg takes to cross. */
  interpDurationMs: number;
  /** Wall-clock ms the last sample that changed `to` arrived. Undefined before the first. */
  sampleAt: number | undefined;
}

interface GroupHandle extends HandleBase {
  readonly kind: "group";
}

interface TextHandle extends HandleBase {
  readonly kind: "text";
  readonly label: Text;
}

interface BackgroundHandle extends HandleBase {
  readonly kind: "background";
  readonly fill: Graphics;
}

interface BarHandle extends HandleBase {
  readonly kind: "bar";
  readonly track: Graphics;
  readonly bar: Graphics;
}

interface CellsHandle extends HandleBase {
  readonly kind: "cells";
  readonly shape: Graphics;
}

interface ShapeHandle extends HandleBase {
  readonly kind: "shape";
  readonly shape: Graphics;
}

/** A surface owns no Pixi drawing: its pixels are the activity's, composited beside this canvas. */
interface SurfaceHandle extends HandleBase {
  readonly kind: "surface";
  readonly nodeKey: string;
}

export type PixiHandle =
  | GroupHandle
  | TextHandle
  | BackgroundHandle
  | BarHandle
  | CellsHandle
  | ShapeHandle
  | SurfaceHandle;

function weightToken(style: StyleTokens): Weight {
  const value = style.font_weight;
  return WEIGHTS.find((weight) => weight === value) ?? "normal";
}

function base(view: Container, parent: Container): HandleBase {
  return {
    view,
    parent,
    destroyed: false,
    root: false,
    baseX: 0,
    baseY: 0,
    baseAlpha: 1,
    centreX: 0,
    centreY: 0,
    pulse: undefined,
    node: undefined,
    fromLeft: 0,
    fromTop: 0,
    fromWidth: 0,
    fromHeight: 0,
    fromFill: 0,
    toLeft: 0,
    toTop: 0,
    toWidth: 0,
    toHeight: 0,
    toFill: 0,
    interpAt: undefined,
    interpDurationMs: 0,
    sampleAt: undefined,
  };
}

function place(view: Container, parent: Container, index: number): void {
  const target = Math.min(index, parent.children.length - 1);
  if (target >= 0 && parent.getChildIndex(view) !== target) parent.setChildIndex(view, target);
}

/** Put a cue's pulse on top of what the draw placed. With no pulse this is the identity. */
function paint(handle: PixiHandle): void {
  const { alpha, scale } = handle.pulse?.at ?? IDENTITY;

  handle.view.scale.set(scale);
  handle.view.alpha = handle.baseAlpha * alpha;
  handle.view.position.set(
    handle.centreX + scale * (handle.baseX - handle.centreX),
    handle.centreY + scale * (handle.baseY - handle.centreY),
  );
}

function lerp(from: number, to: number, fraction: number): number {
  return from + (to - from) * fraction;
}

/** How far through its current leg a handle is, at `now`. 1 once settled: never past `to`. */
function interpFraction(handle: PixiHandle, now: number): number {
  if (handle.interpAt === undefined || handle.interpDurationMs <= 0) return 1;
  const elapsed = now - handle.interpAt;
  if (elapsed <= 0) return 0;
  if (elapsed >= handle.interpDurationMs) return 1;
  return elapsed / handle.interpDurationMs;
}

/** Point a handle's interpolation leg at a freshly drawn node's box and, for a `bar`, its fill. */
function retarget(handle: PixiHandle, node: RenderNode, now: number): void {
  const targetFill = handle.kind === "bar" ? (node.fill ?? 0) : 0;

  if (NOT_INTERPOLATED_KINDS.has(handle.kind)) {
    handle.fromLeft = handle.toLeft = node.box.left;
    handle.fromTop = handle.toTop = node.box.top;
    handle.fromWidth = handle.toWidth = node.box.width;
    handle.fromHeight = handle.toHeight = node.box.height;
    handle.fromFill = handle.toFill = targetFill;
    handle.interpAt = undefined;
    handle.sampleAt = now;
    return;
  }

  const previousSampleAt = handle.sampleAt;
  if (previousSampleAt !== undefined) {
    const unchanged =
      node.box.left === handle.toLeft &&
      node.box.top === handle.toTop &&
      node.box.width === handle.toWidth &&
      node.box.height === handle.toHeight &&
      targetFill === handle.toFill;
    if (unchanged) return;
  }

  const fraction = previousSampleAt === undefined ? 1 : interpFraction(handle, now);
  const gap = previousSampleAt === undefined ? 0 : now - previousSampleAt;

  handle.fromLeft = lerp(handle.fromLeft, handle.toLeft, fraction);
  handle.fromTop = lerp(handle.fromTop, handle.toTop, fraction);
  handle.fromWidth = lerp(handle.fromWidth, handle.toWidth, fraction);
  handle.fromHeight = lerp(handle.fromHeight, handle.toHeight, fraction);
  handle.fromFill = lerp(handle.fromFill, handle.toFill, fraction);

  handle.toLeft = node.box.left;
  handle.toTop = node.box.top;
  handle.toWidth = node.box.width;
  handle.toHeight = node.box.height;
  handle.toFill = targetFill;

  handle.sampleAt = now;
  handle.interpAt = previousSampleAt === undefined ? undefined : now;
  handle.interpDurationMs =
    previousSampleAt === undefined ? 0 : Math.min(MAX_INTERP_MS, Math.max(0, gap));
}

function drawText(handle: TextHandle, node: RenderNode, screen: Screen, x: number, y: number, width: number): void {
  const alignment = alignmentToken(node.style);
  const anchor = anchorFor(alignment);

  handle.label.text = node.text;
  handle.label.style.fill = colorToken(node.style, "color", "#ffffff");
  handle.label.style.fontSize = fontSize(node.style, screen);
  handle.label.style.fontFamily = node.style.font_family ?? "sans-serif";
  handle.label.style.fontWeight = weightToken(node.style);
  handle.label.anchor.set(anchor, 0);
  handle.baseX = x + width * anchor;
  handle.baseY = y;
  handle.baseAlpha = opacity(node.style);
}

/** The layer beneath the scene: one filled rect over the whole canvas. */
function drawBackground(
  handle: BackgroundHandle,
  node: RenderNode,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  handle.fill.clear();
  handle.fill.rect(x, y, width, height);
  handle.fill.fill({
    color: colorToken(node.style, "color", "#000000"),
    alpha: opacity(node.style),
  });
}

function drawBar(
  handle: BarHandle,
  node: RenderNode,
  x: number,
  y: number,
  width: number,
  height: number,
  fill: number,
): void {
  const filled = fill * width;

  handle.track.clear();
  handle.track.rect(x, y, width, height);
  handle.track.fill({
    color: colorToken(node.style, "track_color", "#000000"),
    alpha: numberToken(node.style, "track_opacity", 0.35),
  });

  handle.bar.clear();
  if (filled > 0) {
    handle.bar.rect(x, y, filled, height);
    handle.bar.fill({
      color: colorToken(node.style, "color", "#ffffff"),
      alpha: opacity(node.style),
    });
  }
}

/** One cell of a region: the outline its tiling gave it, filled and stroked from style tokens. */
function drawCells(handle: CellsHandle, node: RenderNode, screen: Screen): void {
  const points = node.points ?? [];

  handle.shape.clear();
  if (points.length < 3) return;

  handle.shape.poly(points.map((one) => ({ x: one.x * screen.width, y: one.y * screen.height })));

  const fillAlpha = numberToken(node.style, "opacity", 0);
  if (fillAlpha > 0) {
    handle.shape.fill({ color: colorToken(node.style, "color", "#ffffff"), alpha: fillAlpha });
  }

  const lineWidth = numberToken(node.style, "line_width", 1);
  const lineAlpha = numberToken(node.style, "line_opacity", 0);
  if (lineWidth > 0 && lineAlpha > 0) {
    handle.shape.stroke({
      color: colorToken(node.style, "line_color", "#ffffff"),
      alpha: lineAlpha,
      width: lineWidth,
    });
  }
}

/** One authored outline filling this node's own box: a rectangle, or an ellipse inscribed in it. */
function drawShape(
  handle: ShapeHandle,
  node: RenderNode,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  handle.shape.clear();
  if (width <= 0 || height <= 0) return;

  if (node.style.form === "ellipse") {
    handle.shape.ellipse(x + width / 2, y + height / 2, width / 2, height / 2);
  } else {
    handle.shape.rect(x, y, width, height);
  }

  // Defaulted to opaque, unlike `cells`, whose zero default is what leaves a board's outlines
  // unfilled. A shape an author added and did not style is a shape they wanted to see.
  const fillAlpha = numberToken(node.style, "opacity", 1);
  if (fillAlpha > 0) {
    handle.shape.fill({ color: colorToken(node.style, "color", "#ffffff"), alpha: fillAlpha });
  }

  const lineWidth = numberToken(node.style, "line_width", 0);
  const lineAlpha = numberToken(node.style, "line_opacity", 1);
  if (lineWidth > 0 && lineAlpha > 0) {
    handle.shape.stroke({
      color: colorToken(node.style, "line_color", "#000000"),
      alpha: lineAlpha,
      width: lineWidth,
    });
  }
}

function build(node: RenderNode, parent: Container): PixiHandle {
  switch (node.kind) {
    case "group": {
      const view = new Container();
      return { ...base(view, parent), kind: "group" };
    }
    case "text": {
      const label = new Text({ text: "" });
      return { ...base(label, parent), kind: "text", label };
    }
    case "background": {
      const fill = new Graphics();
      return { ...base(fill, parent), kind: "background", fill };
    }
    case "bar": {
      const view = new Container();
      const track = new Graphics();
      const bar = new Graphics();
      view.addChild(track, bar);
      return { ...base(view, parent), kind: "bar", track, bar };
    }
    case "cells": {
      const shape = new Graphics();
      return { ...base(shape, parent), kind: "cells", shape };
    }
    case "shape": {
      const shape = new Graphics();
      return { ...base(shape, parent), kind: "shape", shape };
    }
    case "surface": {
      const view = new Container();
      return { ...base(view, parent), kind: "surface", nodeKey: node.key };
    }
  }
}

/** Whether this handle draws no geometry Pixi can hit-test, so its own box has to be the area. */
function needsHitArea(handle: PixiHandle): boolean {
  return handle.root || handle.kind === "surface";
}

/** Draw one handle's geometry at `fraction` through its current leg: a lerp per field, no lookup. */
function paintGeometry(handle: PixiHandle, node: RenderNode, screen: Screen, fraction: number): void {
  const left = lerp(handle.fromLeft, handle.toLeft, fraction);
  const top = lerp(handle.fromTop, handle.toTop, fraction);
  const width = lerp(handle.fromWidth, handle.toWidth, fraction);
  const height = lerp(handle.fromHeight, handle.toHeight, fraction);
  const fill = lerp(handle.fromFill, handle.toFill, fraction);

  const x = left * screen.width;
  const y = top * screen.height;
  const w = width * screen.width;
  const h = height * screen.height;

  handle.centreX = x + w / 2;
  handle.centreY = y + h / 2;

  switch (handle.kind) {
    case "group":
      break;
    case "text":
      drawText(handle, node, screen, x, y, w);
      break;
    case "background":
      drawBackground(handle, node, x, y, w, h);
      break;
    case "bar":
      drawBar(handle, node, x, y, w, h, fill);
      break;
    case "cells":
      drawCells(handle, node, screen);
      break;
    case "shape":
      drawShape(handle, node, x, y, w, h);
      break;
    case "surface":
      break;
  }

  paint(handle);
}

/** Drives a cue's pulse and a handle's in-flight interpolation. A port, so a test owns the clock
 * and nothing waits on a real frame. */
export interface FramePort {
  /** Call `tick` with a monotonic reading each frame until the returned stop is called. */
  readonly start: (tick: (nowMs: number) => void) => () => void;
}

const animationFrames: FramePort = {
  start: (tick) => {
    let live = true;
    const step = (now: number): void => {
      if (!live) return;
      tick(now);
      globalThis.requestAnimationFrame(step);
    };
    globalThis.requestAnimationFrame(step);
    return () => {
      live = false;
    };
  },
};

export interface PixiSceneOptions {
  /** Where OVL-016 surfaces put their own canvases. Absent draws no surface. */
  readonly surfaces?: SurfaceLayer;
  /** Report what a click reached. Absent leaves the canvas inert, as a broadcast wants. */
  readonly onPick?: (node: RenderNode, target: PickTarget) => void;
  readonly frames?: FramePort;
  readonly report?: SceneReport;
  /** The wall clock a sample's arrival is measured against. Testable. */
  readonly now?: () => number;
}

/**
 * Draw into `root`, reading the canvas size at every update.
 *
 * Every container sits at the origin and each node is placed from its absolute normalized box, so
 * a nested node is offset exactly once.
 */
export function createPixiSceneBackend(
  root: Container,
  screen: () => Screen,
  options: PixiSceneOptions = {},
): SceneBackend<PixiHandle> {
  const surfaces = options.surfaces;
  const onPick = options.onPick;
  const frames = options.frames ?? animationFrames;
  const report = options.report ?? sceneReport;
  const clock = options.now ?? (() => performance.now());

  /** The handles a cue is painting right now. */
  const painting = new Set<PixiHandle>();
  /** The handles still animating toward their latest sample. */
  const moving = new Set<PixiHandle>();
  let stopFrames: (() => void) | undefined;

  function ensureFrames(): void {
    stopFrames ??= frames.start(tick);
  }

  function maybeStopFrames(): void {
    if (moving.size > 0 || painting.size > 0) return;
    stopFrames?.();
    stopFrames = undefined;
  }

  function forgetPulse(handle: PixiHandle): void {
    painting.delete(handle);
    maybeStopFrames();
  }

  function forgetMoving(handle: PixiHandle): void {
    moving.delete(handle);
    maybeStopFrames();
  }

  function tick(now: number): void {
    if (moving.size > 0) {
      const view = screen();
      for (const handle of [...moving]) {
        if (handle.destroyed || handle.node === undefined) {
          forgetMoving(handle);
          continue;
        }
        const fraction = interpFraction(handle, now);
        paintGeometry(handle, handle.node, view, fraction);
        if (fraction >= 1) forgetMoving(handle);
      }
    }

    for (const handle of [...painting]) {
      const pulse = handle.pulse;
      if (pulse === undefined || handle.destroyed) {
        forgetPulse(handle);
        continue;
      }
      pulse.startedAt ??= now;
      const elapsed = (now - pulse.startedAt) / pulse.shape.durationMs;
      pulse.at = pulseAt(pulse.shape, elapsed);
      if (elapsed >= 1) {
        handle.pulse = undefined;
        forgetPulse(handle);
      }
      paint(handle);
    }
  }

  function update(handle: PixiHandle, node: RenderNode): void {
    if (handle.destroyed) return;
    place(handle.view, handle.parent, node.index);
    handle.node = node;

    const view = screen();
    const now = clock();
    retarget(handle, node, now);
    const fraction = interpFraction(handle, now);
    if (fraction < 1) {
      moving.add(handle);
      ensureFrames();
    } else {
      forgetMoving(handle);
    }

    if (needsHitArea(handle)) {
      const rect = pixels(node.box, view);
      handle.view.hitArea = new Rectangle(rect.x, rect.y, rect.width, rect.height);
    }

    if (handle.kind === "surface") {
      const ref = node.surface;
      if (ref !== undefined) {
        surfaces?.open(
          handle.nodeKey,
          ref.activityId,
          ref.name,
          node.box,
          ref.budgetMs,
          ref.settings,
        );
      }
    }

    paintGeometry(handle, node, view, fraction);
  }

  /** Report clicks on this handle. A document's backdrop reports the document, not itself. */
  function listen(
    handle: PixiHandle,
    node: RenderNode,
    pick: (node: RenderNode, target: PickTarget) => void,
  ): void {
    if (handle.kind === "background") {
      handle.view.eventMode = "none";
      return;
    }
    handle.view.eventMode = "static";
    handle.view.on("pointertap", (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
      pick(node, handle.root ? "document" : "node");
    });
  }

  return {
    create: (node, parent) => {
      const container = parent === undefined ? root : parent.view;
      const handle = build(node, container);
      handle.root = parent === undefined;
      container.addChild(handle.view);
      if (onPick !== undefined) listen(handle, node, onPick);
      update(handle, node);
      return handle;
    },

    update,

    present: (handle, presentation) => {
      if (handle.destroyed) return;

      const shape = presentationNamed(presentation);
      if (shape === undefined) {
        report.once(
          `presentation:${presentation}`,
          `presentation "${presentation}" is not drawn by this build`,
        );
        return;
      }

      handle.pulse = { shape, startedAt: undefined, at: shape.at(0) };
      paint(handle);
      painting.add(handle);
      ensureFrames();
    },

    remove: (handle) => {
      if (handle.destroyed) return;
      handle.destroyed = true;
      forgetPulse(handle);
      forgetMoving(handle);
      if (handle.kind === "surface") surfaces?.close(handle.nodeKey);
      handle.view.destroy({ children: true });
    },
  };
}
